import React from 'react';
import styled from 'styled-components';
import { TableHeader } from './TableHeader/TableHeader';
import i18next from 'i18next';
import { TableRow } from './TableRow/TableRow';
import { Paginator } from './Paginator/Paginator';
import { TableSearchBox } from './TableSearchBox/TableSearchBox';
import { colors } from '../../styles/colors';
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator';

type Props = {
  header: Row;
  data: Row[];
  onRowClick: (row: Row) => void;
  onPageChange: (page: number, itemsPerPage: number) => void | Promise<void>;
  onSearchTermChange: (newSearchTerm: string) => void;
  search: (searchTerm: string) => void;
  setPage: (page: number) => void;
  loading: boolean;
  rowCount: number;
  currentPage: number;
  searchTerm: string;
  searchPlaceholder: string;
};

export type Row = Record<string, string | number | React.ReactNode>;

export class Table extends React.PureComponent<Props> {
  render() {
    const { header, data, rowCount, loading, currentPage, searchTerm, searchPlaceholder, onPageChange, onSearchTermChange, onRowClick, search, setPage } = this.props;

    return <TableContainer className="Table">
      <Shadow>
        <AboveTable>
          <TableSearchBox searchTerm={searchTerm} onChange={onSearchTermChange} search={search} searchPlaceholder={searchPlaceholder} />
        </AboveTable>
        <PaddedTableBodyContainer>
          <TableGrid>
            <tbody>
              <TableHeader row={header} />
              {
                data.length === 0
                  ? <NoRecordsFoundRow><td colSpan={100}>{i18next.t('cmp.table.error.notFound')}</td></NoRecordsFoundRow>
                  : data.map((row, key) => <TableRow {...{ key, row, onRowClick }} />)
              }
            </tbody>
          </TableGrid>
          <LoadingIndicator show={loading} />
        </PaddedTableBodyContainer>
      </Shadow>
      <BelowTable>
        <Paginator numRecords={rowCount} onChange={onPageChange} currentPage={currentPage} setPage={setPage} />
      </BelowTable>
    </TableContainer>;
  }
}

const Shadow = styled.div`
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  background: ${colors.backgroundLight};
`;

const PaddedTableBodyContainer = styled.div`
  padding-bottom: 0.75rem;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  background: white;
  min-height: calc(2.5rem * 15 + 1.5rem); /* 15 rows + 1.5rem header row*/

  td:first-child,
  th:first-child {
    padding-left: 1.5rem;
  }

  td:last-child,
  th:last-child {
    padding-right: 1.5rem;
  }
`;

const TableGrid = styled.table`
  width: 100%;
  position: relative;
`;

const AboveTable = styled.div`
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  background: white;
  padding: 0.75rem 1.5rem 0 1.5rem;
`;

const BelowTable = styled.div`
  padding: 0.75rem 0;
`;

const NoRecordsFoundRow = styled.tr`
  height: calc(2.5rem * 15);

  td {
    vertical-align: middle;
    text-align: center !important;
  }
`;
